import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	Wrench,
	Search,
	Plus,
	MapPin,
	Package,
	User,
	Menu,
	X,
} from "lucide-react";
import type { User as UserType } from "@/lib/types";

interface HeaderProps {
	currentUser: UserType | null;
	currentView: string;
	onNavigate: (view: string) => void;
}

export function Header({ currentUser, currentView, onNavigate }: HeaderProps) {
	const [mobileOpen, setMobileOpen] = useState(false);

	const navItems = [
		{ id: "discover", label: "Discover", icon: Search },
		{ id: "my-tools", label: "My Tools", icon: Wrench },
		{ id: "bookings", label: "Bookings", icon: Package },
		{ id: "profile", label: "Profile", icon: User },
	];

	return (
		<header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
			<div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
				<button
					onClick={() => onNavigate("discover")}
					className="flex items-center gap-2 hover:opacity-80 transition-opacity"
				>
					<div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
						<Wrench className="w-4 h-4 text-primary-foreground" />
					</div>
					<span className="font-semibold text-lg tracking-tight">ToolShare</span>
				</button>

				<nav className="hidden md:flex items-center gap-1">
					{navItems.map((item) => (
						<Button
							key={item.id}
							variant={currentView === item.id ? "secondary" : "ghost"}
							size="sm"
							onClick={() => onNavigate(item.id)}
							className="gap-2"
						>
							<item.icon className="w-4 h-4" />
							{item.label}
						</Button>
					))}
				</nav>

				<div className="hidden md:flex items-center gap-3">
					<Button
						size="sm"
						onClick={() => onNavigate("list-tool")}
						className="gap-2"
					>
						<Plus className="w-4 h-4" />
						List a Tool
					</Button>
					{currentUser && (
						<button
							onClick={() => onNavigate("profile")}
							className="flex items-center gap-2 hover:opacity-80"
						>
							<Avatar className="h-8 w-8">
								<AvatarImage src={currentUser.avatar} />
								<AvatarFallback>{currentUser.name[0]}</AvatarFallback>
							</Avatar>
							<div className="text-left hidden lg:block">
								<p className="text-sm font-medium leading-none">
									{currentUser.name}
								</p>
								<p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
									<MapPin className="w-3 h-3" />
									{currentUser.neighborhood}
								</p>
							</div>
						</button>
					)}
				</div>

				<Button
					variant="ghost"
					size="sm"
					className="md:hidden"
					onClick={() => setMobileOpen(!mobileOpen)}
				>
					{mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
				</Button>
			</div>

			{mobileOpen && (
				<div className="md:hidden border-t border-border bg-background p-4 space-y-2">
					{navItems.map((item) => (
						<Button
							key={item.id}
							variant={currentView === item.id ? "secondary" : "ghost"}
							className="w-full justify-start gap-2"
							onClick={() => {
								onNavigate(item.id);
								setMobileOpen(false);
							}}
						>
							<item.icon className="w-4 h-4" />
							{item.label}
						</Button>
					))}
					<Button
						className="w-full gap-2"
						onClick={() => {
							onNavigate("list-tool");
							setMobileOpen(false);
						}}
					>
						<Plus className="w-4 h-4" />
						List a Tool
					</Button>
				</div>
			)}
		</header>
	);
}
